import { useMemo, useState } from "react";

import { IListingStatsByPartNumber, IListingStatsByVehicle, IListingStatsResponse } from "../../models/Part";
import classNames from "../../utils/classNames";
import LoadingPulse from "../shared/LoadingPulse";
import { IQueueFilters, isVehicleGroupActive, partNumberGroupChange, vehicleGroupChange } from "./queueFilterState";
import { WorkGroupOrder, formatPriceRange, formatYearSpan, sortWorkGroups } from "./workGroups";

type GroupBy = "partNumbers" | "vehicles";

interface IGroupsToWorkProps {
  stats: IListingStatsResponse | undefined;
  isLoading: boolean;
  filters: IQueueFilters;
  onChange: (next: Partial<IQueueFilters>) => void;
}

const DEFAULT_ROWS = 6;

const segmentClasses = (active: boolean) =>
  classNames(
    "rounded-[5px] px-2.5 py-1 text-xs font-medium transition-colors",
    active ? "bg-primary-600 text-white shadow-sm" : "text-gray-600 hover:bg-gray-100"
  );

const showButtonClasses = (active: boolean) =>
  classNames(
    "rounded-md border px-2.5 py-1 text-xs font-semibold transition",
    active ? "border-primary-600 bg-primary-600 text-white hover:bg-primary-700" : "border-primary-200 bg-white text-primary-700 hover:bg-primary-50"
  );

/**
 * The listing stats by part number and by vehicle (make + model range + category), ranked by how
 * many live listings share the key or by how far apart their prices sit. Show narrows the queue to
 * the group so its listings can be priced together and stay consistent.
 */
export default function GroupsToWork({ stats, isLoading, filters, onChange }: IGroupsToWorkProps) {
  const [groupBy, setGroupBy] = useState<GroupBy>("partNumbers");
  const [order, setOrder] = useState<WorkGroupOrder>("mostListings");
  const [showAll, setShowAll] = useState(false);

  const partNumberRows = useMemo(() => sortWorkGroups(stats?.byPartNumber ?? [], order), [stats?.byPartNumber, order]);
  const vehicleRows = useMemo(() => sortWorkGroups(stats?.byVehicle ?? [], order), [stats?.byVehicle, order]);

  const rowCount = groupBy === "partNumbers" ? partNumberRows.length : vehicleRows.length;
  const visiblePartNumbers = showAll ? partNumberRows : partNumberRows.slice(0, DEFAULT_ROWS);
  const visibleVehicles = showAll ? vehicleRows : vehicleRows.slice(0, DEFAULT_ROWS);

  const renderPartNumberRow = (row: IListingStatsByPartNumber) => {
    const active = filters.partNumber === row.partNumber;
    return (
      <tr key={row.partNumber} className={classNames(active ? "bg-primary-50" : "hover:bg-gray-50")}>
        <td className="px-4 py-2">
          <div className="font-medium text-gray-900">{row.partNumber}</div>
          {row.categoryName && <div className="text-xs text-gray-500">{row.categoryName}</div>}
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-gray-800">{row.totalActive.toLocaleString("en-GB")}</td>
        <td className="px-3 py-2 text-right tabular-nums text-gray-800">{formatPriceRange(row)}</td>
        <td className="px-3 py-2 text-right tabular-nums text-red-700">{row.oldestFirstListedDays > 0 ? `${row.oldestFirstListedDays}d` : "—"}</td>
        <td className="px-4 py-2 text-right">
          <button type="button" onClick={() => onChange(partNumberGroupChange(filters, row.partNumber))} className={showButtonClasses(active)}>
            {active ? "Showing" : "Show"}
          </button>
        </td>
      </tr>
    );
  };

  const renderVehicleRow = (row: IListingStatsByVehicle) => {
    const active = isVehicleGroupActive(filters, row);
    const years = formatYearSpan(row);
    return (
      <tr key={`${row.make}|${row.model}|${row.generation ?? ""}|${row.categoryId}`} className={classNames(active ? "bg-primary-50" : "hover:bg-gray-50")}>
        <td className="px-4 py-2">
          <div className="font-medium text-gray-900">
            {row.make} {row.model}
            {row.generation && <span className="ml-1">{row.generation}</span>}
            {years && <span className="ml-1.5 text-xs font-normal text-gray-500">{years}</span>}
          </div>
          {row.categoryName && <div className="text-xs text-gray-500">{row.categoryName}</div>}
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-gray-800">{row.totalActive.toLocaleString("en-GB")}</td>
        <td className="px-3 py-2 text-right tabular-nums text-gray-800">{formatPriceRange(row)}</td>
        <td className="px-3 py-2 text-right tabular-nums text-red-700">{row.oldestFirstListedDays > 0 ? `${row.oldestFirstListedDays}d` : "—"}</td>
        <td className="px-4 py-2 text-right">
          <button type="button" onClick={() => onChange(vehicleGroupChange(filters, row))} className={showButtonClasses(active)}>
            {active ? "Showing" : "Show"}
          </button>
        </td>
      </tr>
    );
  };

  return (
    <section aria-label="Part numbers and vehicles to work" className="flex min-w-0 flex-col rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-gray-900">Price together</h2>
          <div role="group" aria-label="Group listings by" className="inline-flex rounded-md border border-gray-300 bg-white p-0.5">
            <button
              type="button"
              onClick={() => {
                setGroupBy("partNumbers");
                setShowAll(false);
              }}
              aria-pressed={groupBy === "partNumbers"}
              className={segmentClasses(groupBy === "partNumbers")}
            >
              Part numbers
            </button>
            <button
              type="button"
              onClick={() => {
                setGroupBy("vehicles");
                setShowAll(false);
              }}
              aria-pressed={groupBy === "vehicles"}
              className={segmentClasses(groupBy === "vehicles")}
            >
              Vehicles
            </button>
          </div>
        </div>
        <div role="group" aria-label="Order groups" className="inline-flex rounded-md border border-gray-300 bg-white p-0.5">
          <button type="button" onClick={() => setOrder("mostListings")} aria-pressed={order === "mostListings"} className={segmentClasses(order === "mostListings")}>
            Most listings
          </button>
          <button type="button" onClick={() => setOrder("widestPrices")} aria-pressed={order === "widestPrices"} className={segmentClasses(order === "widestPrices")}>
            Widest prices
          </button>
        </div>
      </div>

      {isLoading && !stats ? (
        <div className="px-4 py-6">
          <LoadingPulse />
        </div>
      ) : rowCount === 0 ? (
        <p className="px-4 py-6 text-sm text-gray-500">
          {groupBy === "partNumbers"
            ? "No part number is shared by two or more live listings yet."
            : "No make, model and category is shared by two or more live listings yet."}
          {stats && !stats.byPartNumber && " Groups appear after the next stats refresh."}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                <th className="px-4 py-2">{groupBy === "partNumbers" ? "Part number" : "Vehicle"}</th>
                <th className="px-3 py-2 text-right">Listings</th>
                <th className="px-3 py-2 text-right">Prices</th>
                <th className="px-3 py-2 text-right">Oldest</th>
                <th className="px-4 py-2 text-right">
                  <span className="sr-only">Show</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {groupBy === "partNumbers" ? visiblePartNumbers.map(renderPartNumberRow) : visibleVehicles.map(renderVehicleRow)}
            </tbody>
          </table>
        </div>
      )}

      {rowCount > DEFAULT_ROWS && (
        <button
          type="button"
          onClick={() => setShowAll((value) => !value)}
          className="border-t border-gray-100 px-4 py-2 text-left text-xs font-medium text-primary-700 hover:bg-gray-50"
        >
          {showAll ? "Show fewer" : `Show all ${rowCount} ${groupBy === "partNumbers" ? "part numbers" : "vehicles"}`}
        </button>
      )}
    </section>
  );
}
